import { useEffect, useMemo, useState } from 'react';
import zxcvbn from 'zxcvbn';

/**
 * Live password strength meter — runs zxcvbn locally (no network round-trip)
 * and shows 5-segment bar + friendly guidance.
 *
 * Score legend:
 *   0 — Too weak (red)
 *   1 — Weak     (red)
 *   2 — Okay     (amber)  ← minimum accepted server-side
 *   3 — Strong   (emerald)
 *   4 — Excellent (deep green)
 */
export function PasswordStrength({ password, className = '', 'data-testid': dt }) {
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!password) { setResult(null); return; }
    // zxcvbn is sync but heavy — defer to idle to keep typing responsive
    const h = setTimeout(() => setResult(zxcvbn(password)), 60);
    return () => clearTimeout(h);
  }, [password]);

  const { score, label, color, crack, tips } = useMemo(() => {
    const s = result?.score ?? 0;
    const labels = ['Too weak', 'Weak', 'Okay', 'Strong', 'Excellent'];
    const colors = ['bg-red-500', 'bg-red-500', 'bg-amber-500', 'bg-emerald-500', 'bg-emerald-600'];
    const crackTime = result?.crack_times_display?.offline_slow_hashing_1e4_per_second || '';
    const warning = result?.feedback?.warning || '';
    const suggestions = (result?.feedback?.suggestions || []).slice(0, 2);
    const tipList = [warning, ...suggestions].filter(Boolean);
    return { score: s, label: labels[s] || '', color: colors[s] || 'bg-navy/20', crack: crackTime, tips: tipList };
  }, [result]);

  if (!password) return null;

  return (
    <div className={`mt-2 ${className}`} data-testid={dt || 'password-strength'}>
      <div className="flex items-center gap-1.5" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i <= score ? color : 'bg-navy/10'}`}
          />
        ))}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-xs">
        <span className={`font-medium ${
          score >= 3 ? 'text-emerald-700' : score === 2 ? 'text-amber-600' : 'text-red-600'
        }`} data-testid="password-strength-label">
          {label}
          {crack ? <span className="text-navy/40 font-normal"> · cracks in {crack}</span> : null}
        </span>
        <span className="text-navy/40" aria-hidden>{password.length} char{password.length === 1 ? '' : 's'}</span>
      </div>
      {tips.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-[11px] text-navy/55 list-disc list-inside">
          {tips.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      )}
    </div>
  );
}

export default PasswordStrength;
